// Co-presence layer — Phase 8 "you're seeing what I'm seeing" demo magic.
//
// Two participants land on the same `?s=<code>` URL → both join the same
// Pusher channel → each broadcasts their look at 10 Hz → each renders the
// other's gaze as a small "ghost cursor" floating in 3D space at the spot
// they're looking at.
//
// Pusher Channels free tier: 200 concurrent connections, 200K msgs/day.
// At 10 Hz × 2 viewers, one demo session = 20 msg/s = 1.2M msg/min — so
// the free tier supports ~10 concurrent demos before throttling. Bump to
// the Startup plan (~$50/mo) when scaling.
//
// Upgrade paths documented at the top of voiceInput.ts; same pattern.

import Pusher from 'pusher-js';
import type { CameraControls } from '../playcanvas/cameraControls';
import { RemoteCursor } from '../playcanvas/remoteCursor';
import { showToast } from './toast';
import type * as pc from 'playcanvas';

const PUBLISH_INTERVAL_MS = 100; // 10 Hz
const PRESENCE_EVENT = 'client-look';
// Discrete UI actions piggyback on the same private channel as the 10 Hz
// camera-look stream. Used for hotspot modal open/close so followers see
// the same info / video / cta / surface popup the host clicked on.
const ACTION_EVENT = 'client-action';

interface LookMessage {
  userId: string;
  displayName: string;
  yaw: number;
  pitch: number;
  hue: number;
  ts: number;
  // Added 2026-05-18 for opt-in follow mode. The peer broadcasts their
  // current room + variant alongside the camera look, so any participant
  // who toggles 🎯 Acompanhar mirrors the whole presented scene, not just
  // the gaze direction. Optional for backwards compat — older clients
  // won't send these fields and follow mode just won't reach room/variant.
  roomId?: string;
  variantId?: string;
}

export interface RemoteSceneInput {
  yaw: number;
  pitch: number;
  roomId?: string;
  variantId?: string;
  /** true on the FIRST apply after entering 🎯 follow mode so the host's
   *  view is snapped (not lerped) into place. Subsequent ticks at 10 Hz
   *  use the smoothed lerp so the guest's camera glides with host. */
  snap?: boolean;
}

/** Discrete UI actions a peer can broadcast — hotspot modal opens / closes.
 *  Routed to followers via `applyRemoteAction`. */
export type RemoteActionInput =
  | { type: 'hotspot-open'; hotspotId: string }
  | { type: 'hotspot-close' };

interface ActionMessage {
  userId: string;
  displayName: string;
  ts: number;
  action: RemoteActionInput;
}

export interface CopresenceDeps {
  app: pc.AppBase;
  camera: pc.Entity;
  controls: CameraControls;
  /** Pusher app key from VITE_PUSHER_KEY. */
  pusherKey: string;
  pusherCluster: string;
  /** Returns the local user's current room+variant so it can ride along on
   *  every published LookMessage. Powers follow-mode receive on peers. */
  getCurrentRoomVariant?: () => { roomId: string; variantId: string } | null;
  /** Applies a remote peer's scene state (camera look + room + variant)
   *  when this user has opted in to 🎯 follow mode. */
  applyRemoteScene?: (input: RemoteSceneInput) => void;
  /** Applies a remote peer's discrete UI action — info / video / cta /
   *  surface hotspot modal open or close — when this user is in 🎯 follow
   *  mode and the action originated from the followed peer. */
  applyRemoteAction?: (input: RemoteActionInput) => void;
}

export class Copresence {
  private readonly deps: CopresenceDeps;
  private pusher: Pusher | null = null;
  private channel: ReturnType<Pusher['subscribe']> | null = null;
  private readonly remoteCursors = new Map<string, RemoteCursor>();
  private readonly userId = randomId();
  private readonly hue = Math.floor(Math.random() * 360);
  private displayName = 'Convidado';
  private publishTimer: number | null = null;
  private joined = false;
  private button: HTMLButtonElement | null = null;
  private currentSessionCode: (() => string | null) | null = null;

  // Follow-mode state. `latestPeers` holds the most-recent LookMessage per
  // remote userId (used to pick which peer to follow + show their name on
  // the toggle button). `followedUserId` is non-null while this local user
  // is mirroring a remote peer's camera + room + variant.
  private readonly latestPeers = new Map<string, LookMessage>();
  private followedUserId: string | null = null;
  private followButton: HTMLButtonElement | null = null;
  // Set to true the very first time we APPLY a remote scene tick — guards
  // the pointerdown disengage handler so the act of "remote setLook
  // dispatching its own internal events" doesn't immediately cancel itself.
  private suppressNextPointerCancel = false;
  // Reset to true every time follow mode is engaged; the first remote-apply
  // tick after that uses snap=true so the host's view materializes instantly.
  // Subsequent ticks use the smooth lerp.
  private firstApplyForFollow = false;

  constructor(deps: CopresenceDeps) {
    this.deps = deps;
  }

  /** Wire the 👥 button. Hides itself when no Pusher key is configured. */
  install(getSessionCode: () => string | null): void {
    this.currentSessionCode = getSessionCode;
    this.button = document.getElementById('copresence-button') as HTMLButtonElement | null;
    if (!this.button) return;
    if (!this.deps.pusherKey) {
      this.button.style.display = 'none';
      console.info('[copresence] VITE_PUSHER_KEY not set — hiding 👥 button');
      return;
    }
    this.button.addEventListener('click', () => void this.toggleJoin());

    // Follow-mode pill — hidden by default; revealed once a peer broadcasts.
    this.followButton = document.getElementById('follow-button') as HTMLButtonElement | null;
    this.followButton?.addEventListener('click', () => this.toggleFollow());

    // Any pointerdown on the engine canvas means the local user wants to
    // look around themselves — disengage follow mode so we don't keep
    // snapping their view back to the host's broadcast.
    const canvas = (this.deps.app.graphicsDevice as unknown as { canvas?: HTMLCanvasElement })?.canvas;
    canvas?.addEventListener('pointerdown', () => {
      if (this.followedUserId && !this.suppressNextPointerCancel) {
        this.setFollowing(null);
      }
      this.suppressNextPointerCancel = false;
    });
  }

  /** Publish a discrete UI action (hotspot modal open/close) to the
   *  private channel. No-op when not joined. Used by App.handleHotspotSelect
   *  + ModalRoot.onClose so followers see the same popup the host clicked.
   *
   *  Safe to call from inside a remote-apply path — there's no echo because
   *  the receive handler filters out messages originating from `userId`. */
  broadcastAction(action: RemoteActionInput): void {
    if (!this.channel || !this.joined) return;
    const msg: ActionMessage = {
      userId: this.userId,
      displayName: this.displayName,
      ts: Date.now(),
      action,
    };
    try {
      this.channel.trigger(ACTION_EVENT, msg);
    } catch (err) {
      // Trigger throws synchronously if client events disabled or the
      // channel hasn't finished subscribing yet — swallow so a missed
      // hotspot broadcast never crashes the local app.
      console.warn('[copresence] broadcastAction failed', err);
    }
  }

  /** Toggle: if not joined, join the current session; else leave. */
  private async toggleJoin(): Promise<void> {
    if (this.joined) {
      this.leave();
      return;
    }
    const sessionCode = this.currentSessionCode?.();
    if (!sessionCode) {
      showToast('Salve uma sessão primeiro (💾) para convidar alguém', { tone: 'info' });
      return;
    }
    const name = (window.prompt('Seu nome (mostra no cursor remoto):', this.displayName)?.trim()) || this.displayName;
    this.displayName = name.slice(0, 18);
    this.join(sessionCode);
  }

  private join(sessionCode: string): void {
    if (this.joined) return;
    if (!this.pusher) {
      this.pusher = new Pusher(this.deps.pusherKey, {
        cluster: this.deps.pusherCluster,
        forceTLS: true,
        // Private-channel auth route. We fold this into /api/sessions/save
        // to stay under the Hobby 12-function cap — the route sniffs
        // socket_id + channel_name to decide between session-save and
        // pusher-auth branches. See api/sessions/save.ts handlePusherAuth.
        authEndpoint: '/api/sessions/save',
      });
    }
    // Private channel — Pusher's protocol silently drops `client-*` events
    // on public channels, so we have to prefix with `private-` and pay the
    // auth round-trip. Verified failure mode: prior to 2026-05-18 the
    // channel was `tour-<code>` (public) and host/guest never saw each
    // other's cursors despite both subscribing successfully.
    const channelName = `private-tour-${sessionCode}`;
    this.channel = this.pusher.subscribe(channelName);

    this.channel.bind(PRESENCE_EVENT, (msg: LookMessage) => {
      if (!msg || msg.userId === this.userId) return; // ignore own echo
      let cursor = this.remoteCursors.get(msg.userId);
      if (!cursor) {
        cursor = new RemoteCursor(this.deps.app, this.deps.camera, msg.displayName, msg.hue ?? 200);
        this.remoteCursors.set(msg.userId, cursor);
      }
      cursor.setLook(msg.yaw, msg.pitch);
      // Update follow-mode tracking + reveal the 🎯 button on first peer
      // appearance. Done AFTER cursor render so the toast for "Acompanhar
      // Foo" can reference the just-arrived name.
      const isNewPeer = !this.latestPeers.has(msg.userId);
      this.latestPeers.set(msg.userId, msg);
      if (isNewPeer) this.updateFollowButton();
      // If this local user has opted in to follow this peer, mirror the
      // broadcast scene state immediately.
      if (this.followedUserId === msg.userId && this.deps.applyRemoteScene) {
        this.suppressNextPointerCancel = true;
        this.deps.applyRemoteScene({
          yaw: msg.yaw,
          pitch: msg.pitch,
          roomId: msg.roomId,
          variantId: msg.variantId,
          snap: this.firstApplyForFollow,
        });
        this.firstApplyForFollow = false;
      }
    });

    // Discrete UI actions (hotspot modal open/close) ride on the same
    // private channel as the 10 Hz camera-look stream. Only routed to the
    // local user's modal/panel system when this user is in 🎯 follow mode
    // AND the action originated from the followed peer — otherwise actions
    // from non-followed peers would surface modals out of nowhere.
    this.channel.bind(ACTION_EVENT, (msg: ActionMessage) => {
      if (!msg || !msg.action || msg.userId === this.userId) return;
      if (this.followedUserId !== msg.userId) return;
      this.deps.applyRemoteAction?.(msg.action);
    });

    this.channel.bind('pusher:subscription_succeeded', () => {
      this.joined = true;
      this.renderButton(true);
      showToast(`Tour compartilhado · convide outros pela URL`, { tone: 'info' });
    });
    this.channel.bind('pusher:subscription_error', (err: { status?: number; type?: string }) => {
      console.error('[copresence] subscription error', err);
      const status = err?.status;
      if (status === 401 || status === 403) {
        showToast('Co-presença: falha na autenticação do canal — verifique PUSHER_SECRET no servidor', { tone: 'error' });
      } else if (status === 501) {
        showToast('Co-presença: auth do servidor não configurada (PUSHER_SECRET ausente)', { tone: 'error' });
      } else {
        showToast(`Co-presença: falha ao entrar (${err?.type ?? 'desconhecido'})`, { tone: 'error' });
      }
      this.leave();
    });

    // Throttled publisher: every PUBLISH_INTERVAL_MS, send our current look.
    this.publishTimer = window.setInterval(() => {
      if (!this.channel) return;
      const look = this.deps.controls.getLook();
      // Ride-along scene state so a peer in 🎯 follow mode mirrors not just
      // the camera but the room + variant the local user is currently in.
      const rv = this.deps.getCurrentRoomVariant?.();
      const msg: LookMessage = {
        userId: this.userId,
        displayName: this.displayName,
        yaw: look.yaw,
        pitch: look.pitch,
        hue: this.hue,
        ts: Date.now(),
        ...(rv ? { roomId: rv.roomId, variantId: rv.variantId } : {}),
      };
      // `client-` events require client events enabled in Pusher dashboard.
      this.channel.trigger(PRESENCE_EVENT, msg);
    }, PUBLISH_INTERVAL_MS);
  }

  private leave(): void {
    if (this.publishTimer !== null) {
      window.clearInterval(this.publishTimer);
      this.publishTimer = null;
    }
    if (this.channel && this.pusher) {
      const name = this.channel.name;
      this.channel.unbind_all();
      this.pusher.unsubscribe(name);
      this.channel = null;
    }
    for (const cursor of this.remoteCursors.values()) cursor.destroy();
    this.remoteCursors.clear();
    this.latestPeers.clear();
    this.followedUserId = null;
    this.updateFollowButton();
    this.joined = false;
    this.renderButton(false);
  }

  /** Toggle the 🎯 follow mode. With no current target, picks the
   *  most-recently-broadcasting peer; with one, disengages. */
  private toggleFollow(): void {
    if (this.followedUserId) {
      this.setFollowing(null);
      return;
    }
    let pick: LookMessage | null = null;
    for (const [uid, msg] of this.latestPeers) {
      if (uid === this.userId) continue;
      if (!pick || msg.ts > pick.ts) pick = msg;
    }
    if (pick) this.setFollowing(pick.userId);
  }

  private setFollowing(userId: string | null): void {
    this.followedUserId = userId;
    this.firstApplyForFollow = userId !== null;
    this.updateFollowButton();
    if (userId) {
      const peer = this.latestPeers.get(userId);
      showToast(`Acompanhando ${peer?.displayName ?? 'participante'} — arraste para sair`, { tone: 'info' });
      // Apply the most-recent broadcast right away so we don't wait up to
      // 100 ms for the next tick to teleport the guest to the host's view.
      if (peer && this.deps.applyRemoteScene) {
        this.suppressNextPointerCancel = true;
        this.deps.applyRemoteScene({
          yaw: peer.yaw,
          pitch: peer.pitch,
          roomId: peer.roomId,
          variantId: peer.variantId,
          snap: true,
        });
        this.firstApplyForFollow = false;
      }
    }
  }

  /** Reveal / hide / restyle the 🎯 button based on (joined? peers? following?). */
  private updateFollowButton(): void {
    if (!this.followButton) return;
    const hasPeer = this.joined && this.latestPeers.size > 0;
    this.followButton.classList.toggle('hidden', !hasPeer);
    if (!hasPeer) return;
    const active = Boolean(this.followedUserId);
    this.followButton.setAttribute('aria-pressed', String(active));
    this.followButton.classList.toggle('is-active', active);
    if (active && this.followedUserId) {
      const peer = this.latestPeers.get(this.followedUserId);
      this.followButton.title = `Sair do modo Acompanhar (${peer?.displayName ?? ''})`;
      this.followButton.setAttribute('aria-label', `Sair do modo Acompanhar ${peer?.displayName ?? ''}`);
    } else {
      this.followButton.title = 'Acompanhar a vista do outro participante — arraste a tela para sair';
      this.followButton.setAttribute('aria-label', 'Acompanhar a vista do outro participante');
    }
  }

  private renderButton(joined: boolean): void {
    if (!this.button) return;
    const icon = this.button.querySelector('.icon-button__icon') as HTMLElement | null;
    if (icon) icon.textContent = joined ? '👥' : '👥';
    this.button.setAttribute('aria-pressed', String(joined));
    this.button.classList.toggle('is-active', joined);
    this.button.setAttribute(
      'aria-label',
      joined ? 'Sair do tour compartilhado' : 'Convidar para tour compartilhado',
    );
  }
}

function randomId(): string {
  return Math.floor(Math.random() * 0xffffffffff).toString(36);
}
